import React from 'react'
import { Checkbox, TableColumnDefinition, Text, tokens } from '@fluentui/react-components'
import { t } from '../i18n/i18n'
import { SwotTypeBadge } from '../components/riskAnalyses/RiskBadges'
import { ActionPlan, ActionPlanStatus, RiskAnalysis, SWOT, SWOTType } from '../api/schemas/schema'
import { IGroupedTableColumnDefintion } from '../interfaces/IGroupedTableColumnDefintion'
import { IGroup } from '../interfaces/IGroup'
import { translateEnumValue } from './enumHelper'
import { BadgeActionPlanStatus } from '../components/demandAnalysis/BadgeActionPlanStatus'
import { formatToSwedishCrowns } from './stringHelper'
import { ISummaryField } from '../interfaces/ISummaryField'
import { GroupHeader } from '../components/common/list/GroupHeader'
import { api } from './apiHelper'
import { IDataContext } from '../interfaces/IDataContext'
import { handleMessage } from './stateHelper'
import { IItemState } from '../interfaces/IItemState'

export enum SWOTTabs {
   Info = 'info',
   Identifying = 'identifying',
   AnalyseEvaluate = 'analyse/evaluate',
   ActionPlan = 'action-plan',
}

export enum SWOTColors {
   Strength = '#9FD89F',
   Weakness = '#EDC978',
   Opportunity = '#9BD9DB',
   Threat = '#EEACB2',
}

export const getSWOTIdentifyColumns = (ellipsisClassname: string): TableColumnDefinition<SWOT>[] => [
   {
      columnId: 'type',
      renderHeaderCell: () => t('Type'),
      renderCell: (item) => <SwotTypeBadge type={item.type} />,
      compare: (a, b) => a.type - b.type,
   },
   {
      columnId: 'title',
      renderHeaderCell: () => t('Title'),
      renderCell: (item) => item.title,
      compare: (a, b) => a.title.localeCompare(b.title),
   },
   {
      columnId: 'description',
      renderHeaderCell: () => t('Description'),
      renderCell: (item) => <Text>{item.description}</Text>,
      compare: (a, b) => a.description.localeCompare(b.description),
   },
]

export const getSWOTTypeColor = (type: SWOTType): string => {
   switch (type) {
      case SWOTType.Strength:
         return SWOTColors.Strength
      case SWOTType.Weakness:
         return SWOTColors.Weakness
      case SWOTType.Opportunity:
         return SWOTColors.Opportunity
      case SWOTType.Threat:
         return SWOTColors.Threat
      default:
         throw new Error(`Unsupported SWOTType: ${type}`)
   }
}

export const getSwotBadgeStyle = (type: SWOTType) => ({
   backgroundColor: getSWOTTypeColor(type),
   color: tokens.colorBrandBackground,
})

export const getTypeLanguageString = (type: string): string => {
   switch (type) {
      case 'Strength':
         return t('Strengths')
      case 'Weakness':
         return t('Weaknesses')
      case 'Opportunity':
         return t('Opportunities')
      case 'Threat':
         return t('Threats')
   }
}

export type ColumnDefinition<T> = {
   key: keyof T
   header?: string
   renderHeaderCell?: () => React.ReactNode
   renderCell?: (value: T[keyof T], item: T) => React.ReactNode
   render?: (value: T[keyof T], item: T) => React.ReactNode
   compare?: (a: T, b: T) => number
   onCellClick?: (value: T[keyof T], item: T) => React.ReactNode
}

export const getSWOTAnalyseEvaluateColumns = (classes: any, disabled: boolean): ColumnDefinition<SWOT>[] => [
   {
      key: 'type',
      renderHeaderCell: () => t('Type'),
      renderCell: (item) => <SwotTypeBadge type={item as number} />,
      compare: (a, b) => a.type - b.type,
   },
   {
      key: 'order',
      renderHeaderCell: () => t('Order'),
      renderCell: (item) => item.toString(),
      compare: (a, b) => a.order - b.order,
   },
   {
      key: 'title',
      renderHeaderCell: () => t('Title'),
      renderCell: (item) => item.toString(),
      compare: (a, b) => a.title.localeCompare(b.title),
   },
   {
      key: 'description',
      renderHeaderCell: () => t('Description'),
      renderCell: (item) => <div className={classes ? classes.ellipsis : ''}>{item.toString()}</div>,
      compare: (a, b) => a.description.localeCompare(b.description),
   },
   {
      key: 'actionRequired',
      renderHeaderCell: () => t('ActionProposal'),
      renderCell: (value, swot) => <Checkbox checked={value as boolean} disabled={disabled || (swot.determined && swot.proposedAction !== null)} />,
   },
]
export const getSWOTActionPlanColumns = (swots: SWOT[]): IGroupedTableColumnDefintion<ActionPlan>[] => [
   {
      columnId: 'title',
      colWidth: 'auto',
      renderHeaderCell: () => t('Title'),
      renderGroupHeaderCell: (group) => {
         return <GroupHeader title={t('SWOT')} badge={<SwotTypeBadge type={Number(group.key)} />} />
      },
      renderCell: (item) => {
         const swot = findSWOTParentToActionPlan(swots, item)
         return swot.title
      },
      compare: () => -1,
   },
   {
      columnId: 'estimatedCost',
      colWidth: 'auto',
      renderHeaderCell: () => t('EstimatedCostShort'),
      renderCell: (item) => {
         const swot = findSWOTParentToActionPlan(swots, item)

         return swot.estimatedCost || swot.estimatedCost === 0 ? formatToSwedishCrowns(swot.estimatedCost) + ' kr' : <i>{t('NoValueSet')}</i>
      },
      renderGroupHeaderCell: (group) => {
         if (group.items.every((x) => findSWOTParentToActionPlan(swots, x).estimatedCost === null)) return <i> {t('NoValueSet')} </i>

         let estimatedCostTotal = 0
         group.items.map((x) => (estimatedCostTotal += findSWOTParentToActionPlan(swots, x).estimatedCost))
         return formatToSwedishCrowns(estimatedCostTotal) + ' kr'
      },
      compare: () => -1,
   },
   {
      columnId: 'budgetedCost',
      colWidth: 'auto',
      compare: (a, b) => -1,
      renderCell: (item) => {
         if (item.status !== ActionPlanStatus.Decided) {
            return <i>{t('NoValueSet')}</i>
         }

         if (item.budgetedCost === null) {
            return <i>{t('NoValueSet')}</i>
         }

         return `${formatToSwedishCrowns(item.budgetedCost)} kr`
      },
      renderHeaderCell: () => t('BudgetedCost'),
      renderGroupHeaderCell: (group) => {
         let budgetedCostTotal = 0
         let hasValues = false

         group.items.forEach((plan) => {
            if (plan.status === ActionPlanStatus.Decided && plan.budgetedCost != null) {
               budgetedCostTotal += plan.budgetedCost
               hasValues = true
            }
         })

         return hasValues ? `${formatToSwedishCrowns(budgetedCostTotal)} kr` : <i>{t('NoValueSet')}</i>
      },
   },
   {
      columnId: 'status',
      colWidth: 'auto',
      renderHeaderCell: () => t('DecisionStatus'),
      renderCell: (item) => <BadgeActionPlanStatus status={item.status} />,
      renderGroupHeaderCell: (group) => {
         const totalAnswered = group.items.filter((item) => item.status !== ActionPlanStatus.NotDecided).length
         const totalItems = group.items.length
         // group.items.map((x) => x.status === ActionPlanStatus.Ongoing)
         return `${totalAnswered} / ${totalItems}`
      },
      compare: () => -1,
   },
]

export const findSWOTParentToActionPlan = (swots: SWOT[], actionPlan: ActionPlan) => swots.find((q) => q.id === actionPlan.swotId)

export const getGroupedSWOTActionPlans = (swots: SWOT[], actionPlans: ActionPlan[]): IGroup<ActionPlan>[] => {
   const groups: IGroup<ActionPlan>[] = []
   actionPlans.forEach((plan) => {
      const swot = swots.find((s) => s.id === plan.swotId)
      const key = swot.type.toString()
      const label = translateEnumValue(SWOTType, swot.type)
      const group = groups.find((g) => g.key === key)
      if (!group) {
         groups.push({ label, key, order: swot.type, hex: '', expanded: true, items: [] })
      }
      groups.find((g) => g.key === key).items.push(plan)
   })
   return groups.sort((a, b) => b.order - a.order)
}

export const getSWOTActionPlanSummaryFields = (actionPlans: ActionPlan[], swots: SWOT[]): ISummaryField[] => {
   let totEstimatedCost = 0
   let hasEstimated = false
   let totBudgetedCost = 0
   let hasBudgeted = false

   actionPlans.forEach((plan) => {
      const risk = swots.find((s) => s.id === plan.swotId)
      if (risk?.estimatedCost != null) {
         totEstimatedCost += risk.estimatedCost
         hasEstimated = true
      }

      if (plan.status === ActionPlanStatus.Decided && plan.budgetedCost != null) {
         totBudgetedCost += plan.budgetedCost
         hasBudgeted = true
      }
   })
   const totalAnswered = actionPlans.filter((plan) => plan.status !== ActionPlanStatus.NotDecided).length
   const totalItems = actionPlans.length
   return [
      {
         label: t('TotalEstimatedCost'),
         text: hasEstimated ? `${formatToSwedishCrowns(totEstimatedCost)} kr` : <i>{t('NoValueSet')}</i>,
      },
      {
         label: t('TotalBudgetedCost'),
         text: hasBudgeted ? `${formatToSwedishCrowns(totBudgetedCost)} kr` : <i>{t('NoValueSet')}</i>,
      },
      { label: t('DecidedMulti'), text: `${totalAnswered} / ${totalItems}` },
   ]
}

export const getFilteredSWOTs = (swots: SWOT[], selectedType: string): SWOT[] => {
   let filteredSWOTs = swots

   if (Object.entries(SWOTType).find(([key, value]) => selectedType === key.toString())) {
      filteredSWOTs = filteredSWOTs.filter((s) => s.type.toString() === selectedType)
   }
   return filteredSWOTs
}

export const groupSWOTsByType = (items: SWOT[]) =>
   items.reduce((groups, item) => {
      const groupKey = SWOTType[item.type]
      if (!groups[groupKey]) {
         groups[groupKey] = []
      }
      groups[groupKey].push(item)
      return groups
   }, {} as Record<string, SWOT[]>)

export const changeOrderOfSwots = async (
   dataContext: IDataContext,
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>,
   analysis: RiskAnalysis,
   reorderedSwots: SWOT[]
) => {
   const { setRootState } = dataContext
   const { userAccount } = dataContext.state

   const updatedAnalysis = {
      ...analysis,
      modifiedById: userAccount.id,
      swoTs: reorderedSwots,
   }
   try {
      const response = await api().updateRiskAnalysis(updatedAnalysis)

      setRiskAnalysisState((prev) => ({
         ...prev,
         item: response.data,
      }))

      handleMessage(setRootState, 'success', t('ItemSuccessfullyUpdated', { Item: t('RiskAssesment') }))
   } catch (error) {
      handleMessage(setRootState, 'error', t('CouldNotUpdateItem', { Item: t('RiskAssesment') }))
   }
}
export const isValidSwotType = (value: number) => {
   return Object.values(SWOTType).includes(value)
}

export const deleteSwot = async (
   swotId: string,
   dataContext: IDataContext,
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
) => {
   try {
      await api().deleteSwot(swotId)

      setRiskAnalysisState((prev) => ({
         ...prev,
         item: {
            ...prev.item,
            swoTs: prev.item.swoTs.filter((s) => s.id !== swotId),
         },
      }))
      handleMessage(dataContext.setRootState, 'success', t('ItemSuccessfullyDeleted', { Item: t('SWOT') }))
   } catch (error: any) {
      let errorMessage = t('CouldNotDeleteItem', { Item: t('SWOT') })
      switch (error.status) {
         case 400:
            errorMessage = t('NoMatchingItemFound', { Item: t('SWOT') })
            break
         case 403:
            errorMessage = t('UserNotAllowedToActionItem', { Action: t('Delete').toLowerCase(), Item: t('SWOT') })
            break
         case 409:
            errorMessage = t('ItemIsConnectedToItemAndCantBeDeleted', { ItemA: t('SWOT'), Article: t('A').toLowerCase(), ItemB: t('ActionPlan').toLowerCase() })
            break
      }
      handleMessage(dataContext.setRootState, 'error', t('CouldNotDeleteItem', { Item: t('SWOT') }), errorMessage)
   }
}
